import * as React from "react";
import { Dropdown } from "@emisgroup/ui-dropdown";
import { FormElement } from "@emisgroup/ui-form";
import { Checkbox } from "@emisgroup/ui-checkbox";
import { Clear } from "@emisgroup/icons-react";
import { ConnectDropTarget, ConnectDragSource } from "react-dnd";
import { useTranslation } from "@emisgroup/application-intl";
import { ActionButton, ActionType, ActionButtonStyle } from "@emisgroup/clint-templates-common";
import { IListItem } from "@emisgroup/clint-templates-perspectives";
import { DragDropSortProps, DragDropSortInstance } from "./addDragDropSort";
import { ActionButtonProperty } from "../types";
import { anyInvalidActionButtonProperties, getActionButtonPropertyValidity } from "../utils/panelActionUtils";

type PanelActionItemProps = DragDropSortProps & {
    initialActionButton: ActionButton;
    invalidProperties: any;
    isReadOnly: boolean;
    setFocus: boolean;
    onRemove: () => void;
    onUpdate: (actionButton: ActionButton) => void;
    isDragging: boolean;
    connectDragSource: ConnectDragSource;
    connectDropTarget: ConnectDropTarget;
};

const PanelActionItem = React.forwardRef<HTMLDivElement, PanelActionItemProps>(
    (
        {
            index,
            initialActionButton,
            invalidProperties,
            setFocus,
            isReadOnly,
            onUpdate,
            onRemove,
            isDragging,
            connectDragSource,
            connectDropTarget,
        }: PanelActionItemProps,
        ref,
    ) => {
        const { t } = useTranslation();

        const [actionButton, setActionButton] = React.useState<ActionButton>(initialActionButton);

        React.useEffect(() => {
            setActionButton(initialActionButton);
        }, [initialActionButton]);

        const actionTypes = React.useMemo<IListItem[]>(
            () =>
                Object.entries(ActionType).map(actionType => ({
                    text: t(`templates.panelActionButtons.${actionType[1]}`),
                    value: actionType[1],
                })),
            [],
        );

        const actionButtonStyles = React.useMemo<IListItem[]>(
            () =>
                Object.entries(ActionButtonStyle).map(actionButtonStyle => ({
                    text: t(`templates.panelActionButtons.${actionButtonStyle[1]}`),
                    value: actionButtonStyle[1],
                })),
            [],
        );

        const updateProperty = (propertyName: string, propertyValue: any) => {
            const { [propertyName]: extractedProp, ...existingValue } = actionButton as any;

            setActionButton({ ...existingValue, [propertyName]: propertyValue });
        };

        const handleTextChange = (propertyName: string) => (e: React.ChangeEvent<HTMLInputElement>) =>
            updateProperty(propertyName, e.currentTarget.value);
        const handleTextBlur = (propertyName: string) => (e: React.FocusEvent<HTMLInputElement>) =>
            updateProperty(propertyName, e.currentTarget.value.trim());
        const handleCheckboxChange = (propertyName: string, checked: boolean) => updateProperty(propertyName, checked);

        // Utility function to check if the actionButton has changed
        const hasActionButtonChanged = (current: ActionButton, initial: ActionButton): boolean => {
            if (current === initial) return false;
            if (typeof current !== "object" || typeof initial !== "object" || current === null || initial === null)
                return true;

            const keys1 = Object.keys(current);
            const keys2 = Object.keys(initial);

            if (keys1.length !== keys2.length) return true;

            for (const key of keys1) {
                if (!keys2.includes(key) || current[key] !== initial[key]) return true;
            }

            return false;
        };

        const handlePanelActionBlur = (e: React.FocusEvent) => {
            const dropdownElements = document.querySelectorAll('[class^="Dropdown_dropdownContent"]');
            if (dropdownElements.length > 0 && dropdownElements[0].contains(e.relatedTarget as Node)) {
                // Prevent calling onUpdate if the blur is caused by interacting with the dropdown
                return;
            }
            // Only call onUpdate if the actionButton state has changed
            if (hasActionButtonChanged(actionButton, initialActionButton)) {
                onUpdate(actionButton);
            }
        };

        const elementRef = React.useRef<HTMLDivElement>(null);
        const panelActionRef = React.useRef<HTMLDivElement>(null);

        if (!isReadOnly) {
            connectDragSource(elementRef);
            connectDropTarget(elementRef);
        }

        const opacity = isDragging ? 0 : 1;

        React.useImperativeHandle<any, DragDropSortInstance>(ref, () => ({
            getNode: () => elementRef.current,
        }));

        React.useEffect(() => {
            if (setFocus && panelActionRef.current) {
                (panelActionRef.current.childNodes[0].childNodes[0] as HTMLSelectElement).focus();
            }
        }, [setFocus]);

        const { isValid: isLabelValid, message: labelErrorMessage } = getActionButtonPropertyValidity(
            ActionButtonProperty.LABEL,
            invalidProperties,
        );

        const panelActionItemClass = `panel-action-item ${
            anyInvalidActionButtonProperties(invalidProperties) ? "panel-action-item--invalid" : ""
        }`;

        // Shared helper to update actionButton and call onUpdate
        const updateActionButtonProperty = <K extends keyof ActionButton>(property: K, value: ActionButton[K]) => {
            const newActionButton = { ...actionButton, [property]: value };
            setActionButton(newActionButton);
            onUpdate(newActionButton);
        };

        return (
            <div
                data-testid={`panel-action-item-${index}`}
                ref={elementRef}
                className={panelActionItemClass}
                style={{ opacity }}
            >
                <div
                    data-testid={`panel-action-item-grid-${index}`}
                    className="panel-actions__grid"
                    onBlur={handlePanelActionBlur}
                >
                    <span ref={panelActionRef} className="panel-actions__grid__action">
                        <Dropdown
                            data-testid={`panel-action-type-${index}`}
                            onValueChange={value => {
                                updateActionButtonProperty("action", value as ActionType);
                            }}
                            value={actionButton.action}
                            disabled={isReadOnly}
                        >
                            <Dropdown.Trigger aria-label={t("templates.panelActionButtons.selectAction")} />
                            <Dropdown.Content>
                                {actionTypes.map(option => (
                                    <Dropdown.Item key={option.text} value={option.value.toString()}>
                                        {option.text}
                                    </Dropdown.Item>
                                ))}
                            </Dropdown.Content>
                        </Dropdown>
                    </span>

                    <FormElement
                        className="panel-actions__grid__label"
                        errorText={isLabelValid ? undefined : labelErrorMessage ?? " "}
                    >
                        <input
                            className={`custom-text-input${!isLabelValid ? " custom-text-input--invalid" : ""}`}
                            aria-label={t("templates.panelActionButtons.enterLabel")}
                            onChange={handleTextChange("label")}
                            onBlur={handleTextBlur("label")}
                            value={actionButton.label}
                            readOnly={isReadOnly}
                            data-testid={`panel-action-label-${index}`}
                        />
                    </FormElement>

                    <span className="panel-actions__grid__style">
                        <Dropdown
                            data-testid={`panel-action-style-${index}`}
                            onValueChange={value => {
                                updateActionButtonProperty("style", value as ActionButtonStyle);
                            }}
                            value={actionButton.style}
                            disabled={isReadOnly}
                        >
                            <Dropdown.Trigger aria-label={t("templates.panelActionButtons.selectStyle")} />
                            <Dropdown.Content>
                                {actionButtonStyles.map(option => (
                                    <Dropdown.Item key={option.text} value={option.value.toString()}>
                                        {option.text}
                                    </Dropdown.Item>
                                ))}
                            </Dropdown.Content>
                        </Dropdown>
                    </span>

                    <span className="panel-actions__grid__validate">
                        <Checkbox
                            data-testid={`panel-action-validate-${index}`}
                            className="panel-actions__grid__validate"
                            onChange={checked => handleCheckboxChange("validate", checked)}
                            checked={actionButton.validate}
                            disabled={isReadOnly}
                        />
                    </span>

                    <span className="panel-actions__grid__remove">
                        {!isReadOnly && (
                            <button
                                type="button"
                                aria-label={t("templates.panelActionButtons.removePanelActionButton")}
                                data-testid={`panel-action-remove-button-${index}`}
                                className="btn btn-default remove-option-button"
                                onClick={onRemove}
                            >
                                <Clear title={t("templates.panelActionButtons.removePanelActionButton")} />
                            </button>
                        )}
                    </span>
                </div>
            </div>
        );
    },
);

export default PanelActionItem;
